import { useState, useEffect, useCallback } from "react";
import { handleApiError, showApiError } from "../utils/errorHandling";

type AnyFunction = (...args: unknown[]) => Promise<unknown> | unknown;

interface UseApiCallOptions<T> {
  immediate?: boolean;
  onSuccess?: (res: T) => void;
  onError?: (err: unknown) => void;
  showErrorToast?: boolean;
}

interface UseMutationOptions<T> {
  onSuccess?: (res: T) => void;
  onError?: (err: unknown) => void;
  showErrorToast?: boolean;
}

interface UsePaginatedApiOptions<T> {
  pageSize?: number;
  onSuccess?: (res: T) => void;
  onError?: (err: unknown) => void;
  showErrorToast?: boolean;
}

interface PaginatedApiResult<T> {
  data?: T[];
  hasMore?: boolean;
}

export function useApiCall<T = unknown>(
  apiFunction: AnyFunction,
  dependencies: unknown[] = [],
  options: UseApiCallOptions<T> = {}
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const { immediate = true, onSuccess, onError, showErrorToast = true } = options;

  const execute = useCallback(
    async (...args: unknown[]) => {
      try {
        setLoading(true);
        setError(null);

        const result = await apiFunction(...args);
        setData(result as T);

        if (onSuccess) {
          onSuccess(result as T);
        }

        return result;
      } catch (err) {
        const apiError = typeof handleApiError === "function" ? handleApiError(err) : err;
        setError(apiError);

        if (showErrorToast) {
          showApiError(apiError);
        }

        if (onError) {
          onError(apiError);
        }

        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [apiFunction, onSuccess, onError, showErrorToast]
  );

  useEffect(() => {
    if (immediate) {
      // execute but don't await here
       
      execute();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  return {
    data,
    loading,
    error,
    execute,
    reset,
  };
}

export function useMutation<T = unknown>(apiFunction: AnyFunction, options: UseMutationOptions<T> = {}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const {
    onSuccess,
    onError,
    showErrorToast = true,
  } = options;

  const mutate = useCallback(
    async (...args: unknown[]) => {
      try {
        setLoading(true);
        setError(null);

        const result = await apiFunction(...args);

        if (onSuccess) {
          onSuccess(result as T);
        }

        return result;
      } catch (err) {
        const apiError = typeof handleApiError === "function" ? handleApiError(err) : err;
        setError(apiError);

        if (showErrorToast) {
          showApiError(apiError);
        }

        if (onError) {
          onError(apiError);
        }

        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [apiFunction, onSuccess, onError, showErrorToast]
  );

  const reset = useCallback(() => {
    setError(null);
    setLoading(false);
  }, []);

  return {
    mutate,
    loading,
    error,
    reset,
  };
}

export function usePaginatedApi<T = unknown>(
  apiFunction: AnyFunction,
  initialParams: Record<string, unknown> = {},
  options: UsePaginatedApiOptions<PaginatedApiResult<T>> = {}
) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  const { pageSize = 20, onSuccess, onError, showErrorToast = true } = options;

  const loadMore = useCallback(
    async (reset = false) => {
      try {
        setLoading(true);
        setError(null);

        const currentPage = reset ? 1 : page;
        const params = { ...initialParams, page: currentPage, limit: pageSize };

        const result = await apiFunction(params) as PaginatedApiResult<T>;

        if (reset) {
          setData(result.data || []);
          setPage(2);
        } else {
          setData((prev) => [...prev, ...(result.data || [])]);
          setPage((prev) => prev + 1);
        }

        setHasMore(result.hasMore || false);

        if (onSuccess) {
          onSuccess(result);
        }

        return result;
      } catch (err) {
        const apiError = typeof handleApiError === "function" ? handleApiError(err) : err;
        setError(apiError);

        if (showErrorToast) {
          showApiError(apiError);
        }

        if (onError) {
          onError(apiError);
        }

        throw apiError;
      } finally {
        setLoading(false);
      }
    },
    [apiFunction, initialParams, page, pageSize, onSuccess, onError, showErrorToast]
  );

  const refresh = useCallback(() => {
    return loadMore(true);
  }, [loadMore]);

  useEffect(() => {
     
    loadMore(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    loading,
    error,
    hasMore,
    loadMore: () => loadMore(false),
    refresh,
  };
}
